
import React, { useState } from 'react';
import { useSales } from './hooks/useSales';

interface CartItem {
  id: string;
  product: any;
  quantity: number;
  price: number;
  total: number;
}

interface ShoppingCartProps {
  customerId?: string;
  onCheckout: (cartItems: CartItem[], totals: any) => void;
  onClose: () => void;
}

export const ShoppingCart: React.FC<ShoppingCartProps> = ({
  customerId,
  onCheckout,
  onClose
}) => {
  const { cartItems, updateCartItem, removeFromCart, clearCart, getCartTotals } = useSales();
  const [advancePercentage, setAdvancePercentage] = useState(15);
  
  const totals = getCartTotals();
  const advanceAmount = (totals.total * advancePercentage) / 100;
  const remainingAmount = totals.total - advanceAmount;

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
    } else {
      updateCartItem(itemId, newQuantity);
    }
  };

  const handleCheckout = () => {
    const checkoutData = {
      items: cartItems,
      totals: {
        ...totals,
        advanceAmount,
        remainingAmount,
        advancePercentage
      }
    };
    onCheckout(cartItems, checkoutData.totals);
  };

  if (cartItems.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <div className="text-center">
            <i className="ri-shopping-cart-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-lg font-semibold mb-2">Your Cart is Empty</h3>
            <p className="text-gray-600 mb-6">Add some products to get started</p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 whitespace-nowrap"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-primary">Shopping Cart</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-6">
          {/* Cart Items */}
          <div className="space-y-4 mb-6">
            {cartItems.map((item) => (
              <div key={item.id} className="flex items-center space-x-4 p-4 border rounded-lg">
                <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={item.product.images?.[0] || `https://public.readdy.ai/ai/img_res/45d2b43cea4df421f42a36be8e3c6709.jpg$%7Bitem.product.category%7D%20jewelry%20$%7Bitem.product.metal_type%7D%20on%20white%20background%20professional%20product%20photography&width=200&height=200&seq=${item.id}&orientation=squarish`}
                    alt={item.product.model_name}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{item.product.model_name}</h3>
                  <p className="text-sm text-gray-600">{item.product.category} • {item.product.metal_type}</p>
                  <p className="text-sm text-gray-500">by {item.product.vendor_name}</p>
                  <div className="flex items-center mt-2">
                    <span className="text-lg font-bold text-primary">₹{item.price.toLocaleString()}</span>
                    <span className="text-sm text-gray-500 ml-2">each</span>
                  </div>
                </div>

                {/* Quantity Controls */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                    className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300"
                  >
                    <i className="ri-subtract-line text-sm"></i>
                  </button>
                  <span className="w-12 text-center font-medium">{item.quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                    className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300"
                  >
                    <i className="ri-add-line text-sm"></i>
                  </button>
                </div>

                {/* Item Total */}
                <div className="text-right">
                  <div className="text-lg font-bold text-primary">₹{item.total.toLocaleString()}</div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="text-red-500 hover:text-red-700 text-sm mt-1"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Cart Summary */}
          <div className="border-t pt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Payment Options */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Payment Options</h3>
                
                {/* Advance Payment Slider */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <label className="font-medium text-gray-700">Advance Payment</label>
                    <span className="text-sm text-gray-600">{advancePercentage}%</span>
                  </div>
                  <input
                    type="range"
                    min="15"
                    max="100"
                    value={advancePercentage}
                    onChange={(e) => setAdvancePercentage(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>15% (Minimum)</span>
                    <span>100% (Full Payment)</span>
                  </div>
                </div>

                {/* Payment Benefits */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Payment Benefits</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li className="flex items-center">
                      <i className="ri-check-line mr-2 text-green-600"></i>
                      {advancePercentage >= 85 ? 'Gold rate frozen at current price' : 'Gold rate subject to market changes'}
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line mr-2 text-green-600"></i>
                      Order confirmation and production start
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line mr-2 text-green-600"></i>
                      Priority delivery scheduling
                    </li>
                  </ul>
                </div>
              </div>

              {/* Order Summary */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Order Summary</h3>
                
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span>Subtotal ({totals.totalItems} items)</span>
                    <span>₹{totals.subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Making Charges</span>
                    <span>₹{totals.makingCharges.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>GST (3%)</span>
                    <span>₹{totals.gst.toLocaleString()}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between text-lg font-bold">
                    <span>Total Amount</span>
                    <span className="text-primary">₹{totals.total.toLocaleString()}</span>
                  </div>
                  
                  <div className="border-t pt-3 space-y-2">
                    <div className="flex justify-between text-green-600">
                      <span>Advance Payment ({advancePercentage}%)</span>
                      <span className="font-semibold">₹{advanceAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-orange-600">
                      <span>Balance on Delivery</span>
                      <span className="font-semibold">₹{remainingAmount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Delivery Information */}
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 mb-2">Delivery Timeline</h4>
                  <div className="text-sm text-yellow-800">
                    <div className="flex items-center mb-1">
                      <i className="ri-time-line mr-2"></i>
                      Expected delivery: {Math.max(...cartItems.map(item => item.product.delivery_days))} days
                    </div>
                    <div className="flex items-center">
                      <i className="ri-map-pin-line mr-2"></i>
                      Delivery to store for pickup
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4 mt-6 pt-6 border-t">
              <button
                onClick={() => clearCart()}
                className="px-6 py-3 border border-red-300 text-red-600 rounded-md hover:bg-red-50 whitespace-nowrap"
              >
                <i className="ri-delete-bin-line mr-2"></i>
                Clear Cart
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 whitespace-nowrap"
              >
                Continue Shopping
              </button>
              <button
                onClick={handleCheckout}
                className="flex-1 px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90 whitespace-nowrap"
                disabled={!customerId}
              >
                <i className="ri-secure-payment-line mr-2"></i>
                Proceed to Checkout
              </button>
            </div>
            
            {!customerId && (
              <p className="text-red-500 text-sm mt-2 text-center">
                Please select a customer before proceeding to checkout
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
